import React, { useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../shared/auth.interceptor';
import { doLogin } from '../../shared/auth.functions';
import { apiMessage, errorToast } from '../../shared/toast.service';
import { initializeApp } from '../../../app.init';
import { MainRoute } from '../../../app';
import { UserRoute } from '../../user/userContainer';

export interface IntuitLinkRequest {
  email: string;
  linkToken: string;
}

interface Props {
  request: IntuitLinkRequest | null;
  onClose: () => void;
}

/**
 * Shown when someone signs in with Intuit using an email that already has a RoboDebit
 * account. Their password proves the account is theirs before the two are linked.
 */
const LinkIntuitDialog: React.FC<Props> = ({ request, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const close = () => {
    setPassword('');
    onClose();
  };

  const link = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!request || !password) return;
    setBusy(true);
    try {
      const response = await apiClient.post('/v1/auth/intuit/link', { linkToken: request.linkToken, password });
      doLogin(response.data);
      close();
      await dispatch(initializeApp() as any);
      navigate(`${MainRoute.User}${UserRoute.Clients}`);
    } catch (error) {
      errorToast(apiMessage(error, 'Could not link your Intuit sign-in. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!request} onClose={close} maxWidth="xs" fullWidth>
      <form onSubmit={link}>
        <DialogTitle>Link Sign in with Intuit</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            A RoboDebit account already uses <b>{request?.email}</b>. Enter its RoboDebit password to link it. After that,
            Sign in with Intuit opens this account.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="password"
            label="RoboDebit password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={!password || busy}>
            Link and Sign In
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default LinkIntuitDialog;
